import { ISceneLoaderProgressEvent, SceneLoader } from '@babylonjs/core/Loading/sceneLoader.js'
import { Mesh } from '@babylonjs/core/Meshes/mesh.js'
import { Scene } from '@babylonjs/core/scene.js'
import { Nullable } from '@babylonjs/core/types.js'
import React, { useContext, useState } from 'react'
import { CreatedInstance } from '../../CreatedInstance'
import { FiberMesh } from '../../generatedCode'
import { FiberMeshProps } from '../../generatedProps'
import { PropertyUpdate, PropsHandler } from '../../PropsHandler'
import { applyUpdateToInstance } from '../../UpdateInstance'
import { useScene } from '../scene'
import { ILoadedModel, LoadedModel, LoaderStatus } from './loadedModel'

export type SceneLoaderContextType =
  | {
      updateProgress: (progress: ISceneLoaderProgressEvent) => void
      lastProgress?: Nullable<ISceneLoaderProgressEvent>
    }
  | undefined

export const SceneLoaderContext = React.createContext<SceneLoaderContextType>(undefined)

export type SceneLoaderContextProviderProps = {
  startProgress?: ISceneLoaderProgressEvent
  children: React.ReactNode
}

export const SceneLoaderContextProvider: React.FC<SceneLoaderContextProviderProps> = (
  props: SceneLoaderContextProviderProps
) => {
  const [progress, setProgress] = useState<Nullable<ISceneLoaderProgressEvent>>(null)

  return (
    <SceneLoaderContext.Provider value={{ lastProgress: progress, updateProgress: setProgress }}>
      {props.children}
    </SceneLoaderContext.Provider>
  )
}

export type SceneLoaderOptions = {
  /**
   * An array of mesh names, a single mesh name, or empty string for all meshes that filter what meshes are imported
   */
  meshNames?: any

  /**
   * set that all meshes receive shadows.
   * Defaults to false.
   */
  receiveShadows?: boolean

  /**
   * Scale entire model within these square bounds
   * Defaults to no scaling.
   */
  scaleToDimension?: number

  /**
   * Always select root mesh as active.
   * Defaults to false.
   */
  alwaysSelectAsActiveMesh?: boolean

  /**
   * SceneLoader progress events are set on context provider (when available).
   * Defaults to false.
   */
  reportProgress?: boolean

  /**
   * Not needed if you are within a SceneContext.
   */
  scene?: Scene

  /**
   * Access to loaded model as soon as it is loaded, so it provides a way to hide or scale the meshes before the first render.
   */
  onModelLoaded?: (loadedModel: ILoadedModel) => void

  /**
   * Raw progress event for SceneLoader
   */
  onLoadProgress?: (event: ISceneLoaderProgressEvent) => void

  /**
   * Called if SceneLoader returns an error.
   */
  onModelError?: (message: string, exception?: any) => void
}

/**
 * A cached version of SceneLoader with a Suspense cache.
 */
const useSceneLoaderWithCache = (): ((
  rootUrl: string,
  sceneFilename: string,
  pluginExtension?: string,
  options?: SceneLoaderOptions,
  initialProps?: FiberMeshProps
) => LoadedModel) => {
  // we need our own memoized cache. useMemo, useState, etc. fail miserably - throwing a promise forces the component to remount.
  let suspenseCache: Record<string, undefined | (() => LoadedModel)> = {}
  let suspenseScene: Nullable<Scene> = null

  // let tasksCompletedCache: Record<string, LoadedModel> = {};

  return (
    rootUrl: string,
    sceneFilename: string,
    pluginExtension?: string,
    options?: SceneLoaderOptions,
    initialProps?: FiberMeshProps
  ): LoadedModel => {
    const opts: SceneLoaderOptions = options || {}

    // we are not in a callback here
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const hookScene = useScene()

    if (opts.scene === undefined && hookScene === null) {
      throw new Error(
        'useSceneLoader can only be used inside a Scene component (or pass scene as an option)'
      )
    }

    const scene: Scene = opts.scene || hookScene!

    if (suspenseScene == null) {
      suspenseScene = scene
    } else {
      if (suspenseScene !== scene) {
        // console.log('new scene detected - clearing useAssetManager cache');
        suspenseCache = {}
        // NOTE: could keep meshes with mesh.serialize and Mesh.Parse
        // Need to research how to do with textures, etc.
        // browser cache should make the load fast in most cases
        // tasksCompletedCache = {};
        suspenseScene = scene
      }
    }

    const suspenseKey = `${rootUrl}/${sceneFilename}`
    // we are not in a callback here
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const sceneLoaderContext = useContext<SceneLoaderContextType>(SceneLoaderContext)

    const createSceneLoader = (): (() => LoadedModel) => {
      const taskPromise = new Promise<LoadedModel>((resolve, reject) => {
        const loadedModel = new LoadedModel()

        loadedModel.status = LoaderStatus.Loading

        // /BabylonJS/Babylon.js/pull/15798
        // NOTE: consider making this callback available as a prop
        SceneLoader.OnPluginActivatedObservable.addOnce((callback) => {
          loadedModel.loaderName = callback.name
        })

        /* const loader: Nullable<ISceneLoaderPlugin | ISceneLoaderPluginAsync> = */
        SceneLoader.ImportMeshAsync(
          opts.meshNames,
          rootUrl,
          sceneFilename,
          scene,
          (event: ISceneLoaderProgressEvent): void => {
            if (opts.reportProgress === true && sceneLoaderContext !== undefined) {
              sceneLoaderContext!.updateProgress(event)
            }
            if (opts.onLoadProgress) {
              opts.onLoadProgress(event)
            }
          },
          pluginExtension
        )
          .then((result) => {
            loadedModel.rootMesh = new Mesh(sceneFilename + '-root-model', scene)
            if (opts.alwaysSelectAsActiveMesh === true) {
              loadedModel.rootMesh.alwaysSelectAsActiveMesh = true
            }

            loadedModel.meshes = []
            result.meshes.forEach((mesh) => {
              loadedModel.meshes!.push(mesh)

              // leave meshes already parented to maintain model hierarchy:
              if (!mesh.parent) {
                mesh.parent = loadedModel.rootMesh!
              }

              if (opts.receiveShadows === true) {
                mesh.receiveShadows = true
              }
            })
            loadedModel.particleSystems = result.particleSystems
            loadedModel.skeletons = result.skeletons
            loadedModel.animationGroups = result.animationGroups

            // NOTE: geometries, lights, spriteManagers, etc. can be added here
            loadedModel.status = LoaderStatus.Loaded

            if (opts.scaleToDimension) {
              loadedModel.scaleTo(opts.scaleToDimension)
            }

            if (initialProps) {
              const initPayload: PropertyUpdate[] = []
              new FiberMesh().getPropsHandlers().forEach((propHandler: PropsHandler<any>) => {
                const handlerUpdates: PropertyUpdate[] | null = propHandler.getPropertyUpdates(
                  {}, // Here we will reapply things like 'name', so we could get default props from 'babylonObject'.
                  initialProps
                )
                if (handlerUpdates !== null) {
                  initPayload.push(...handlerUpdates)
                }
              })

              if (initPayload.length > 0) {
                initPayload.forEach((update) => {
                  applyUpdateToInstance(
                    { hostInstance: loadedModel.rootMesh } as CreatedInstance<Mesh>,
                    update
                  )
                })
              }
            }

            if (options?.onModelLoaded) {
              options.onModelLoaded(loadedModel)
            }

            const originalDispose = loadedModel.dispose
            loadedModel.dispose = () => {
              // console.log('Clearing cache (cannot re-use meshes).');
              suspenseCache[suspenseKey] = undefined
              originalDispose.call(loadedModel)
            }
            resolve(loadedModel)
          })
          .catch((error) => {
            if (opts.onModelError) {
              opts.onModelError(error)
            }
            reject(error)
          })
      })

      let result: LoadedModel
      let error: Nullable<Error> = null
      let suspender: Nullable<Promise<void>> = null
      suspender = (async () => {
        try {
          result = await taskPromise
        } catch (e) {
          error = e as Error
        } finally {
          suspender = null
        }
      })()

      const getAssets = () => {
        if (suspender) {
          throw suspender
        }
        if (error !== null) {
          throw error
        }

        return result
      }
      return getAssets
    }

    if (suspenseCache[suspenseKey] === undefined) {
      suspenseCache[suspenseKey] = createSceneLoader()
    }

    return suspenseCache[suspenseKey]!()
  }
}

// this isn't a hook being called
// eslint-disable-next-line react-hooks/rules-of-hooks
export const useSceneLoader = useSceneLoaderWithCache()
